/** Raw value of `route.query.redirect` (vue-router: string | null or repeated → array). */
export type RedirectQueryValue =
  | string
  | null
  | undefined
  | ReadonlyArray<string | null>;

function firstRedirectString(raw: RedirectQueryValue): string | null {
  if (raw == null) {
    return null;
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (typeof item === 'string') {
        return item;
      }
    }
    return null;
  }
  return raw;
}

/**
 * Returns a same-origin path (pathname + search + hash) safe for navigation,
 * or null if the value must not be used (open redirect, javascript:, etc.).
 * Accepts a string or the raw `route.query.redirect` shape (string | string[]).
 *
 * **Browser-only:** uses `globalThis.location.origin`; in non-browser contexts the inner check
 * fails and this returns `null`.
 */
export function sanitizeInternalRedirect(
  raw: RedirectQueryValue,
): string | null {
  const single = firstRedirectString(raw);
  if (single == null) {
    return null;
  }
  const trimmed = single.trim();
  if (trimmed === '') {
    return null;
  }

  let decoded: string;
  try {
    decoded = decodeURIComponent(trimmed);
  } catch {
    return null;
  }

  if (!decoded.startsWith('/')) {
    return null;
  }
  if (decoded.startsWith('//')) {
    return null;
  }
  if (decoded.includes('://')) {
    return null;
  }

  try {
    const origin = globalThis.location.origin;
    const url = new URL(decoded, origin);
    if (url.origin !== origin) {
      return null;
    }
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}
